import { Component, OnInit, ViewChild } from "@angular/core";
import { AreaContactDto, AreaContactSaveState } from "../models/AreaContactDto";
import { AreaContactService } from "../services/area-contact.service";
import { ToastService } from "src/app/shared/toast.service";
import { DeviceCheckService } from "src/app/core/survey/shared/services/device-check.service";
import { ConfirmDialogComponent } from "src/app/shared/confirm-dialog/confirm-dialog.component";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { first } from "rxjs/operators";
import { Table } from "primeng/table";
import {
  EmailRegexString,
  PhoneNumberRegexString,
} from "src/app/shared/regex/regexes";
import { FilterableTableComponent } from "src/app/shared/table/filterable-table.component";
import { ColumnTypes } from "src/app/shared/Models/enums/ColumnTypeEnum";
import { NetworkOwnerService } from "src/app/shared/services/network-owner.service";
import { NetworkOwner } from "src/app/shared/Models/NetworkOwner";

@Component({
  selector: "app-admin-panel-fiber-hood-contacts",
  templateUrl: "./admin-panel-fiber-hood-contacts.component.html",
  styleUrls: ["./admin-panel-fiber-hood-contacts.component.scss"],
})
export class AdminPanelFiberHoodContactsComponent
  extends FilterableTableComponent<AreaContactDto>
  implements OnInit
{
  @ViewChild("dt") dataTable: Table;

  clonedAreaContacts: { [s: string]: AreaContactDto } = {};
  networkOwners: NetworkOwner[] = [];

  uploadStates = AreaContactSaveState;

  isTableInEditMode: boolean = false;
  hasValidationErrors: boolean = false;

  scrollOffsetLandscape: number = 210;
  scrollOffsetPortrait: number = 200;

  allCols = [
    {
      field: "city",
      header: "CITY",
      visibleOnPortrait: true,
      hasFilter: true,
      type: ColumnTypes.STRING,
      template: "default",
      filterSearch: true,
      hasEmptyValue: false,
    },
    {
      field: "fiberHood",
      header: "FH",
      visibleOnPortrait: true,
      hasFilter: true,
      type: ColumnTypes.STRING,
      template: "default",
      filterSearch: true,
      hasEmptyValue: false,
    },
    {
      field: "firstName",
      header: "FIRST NAME",
      visibleOnPortrait: true,
      hasFilter: true,
      type: ColumnTypes.STRING,
      template: "default",
      filterSearch: true,
      hasEmptyValue: true,
    },
    {
      field: "lastName",
      header: "LAST NAME",
      visibleOnPortrait: true,
      hasFilter: true,
      type: ColumnTypes.STRING,
      template: "default",
      filterSearch: true,
      hasEmptyValue: true,
    },
    {
      field: "email",
      header: "EMAIL",
      visibleOnPortrait: true,
      hasFilter: true,
      type: ColumnTypes.STRING,
      template: "default",
      filterSearch: true,
      hasEmptyValue: true,
    },
    {
      field: "phoneNumber",
      header: "PHONE NUMBER",
      visibleOnPortrait: true,
      hasFilter: true,
      type: ColumnTypes.STRING,
      template: "default",
      filterSearch: true,
      hasEmptyValue: true,
    },
    {
      field: "ownerName",
      header: "NETWORK OWNER",
      visibleOnPortrait: true,
      hasFilter: true,
      type: ColumnTypes.STRING,
      template: "default",
      filterSearch: true,
      hasEmptyValue: true,
    },
  ];

  constructor(
    private readonly modalService: NgbModal,
    private readonly areaContactService: AreaContactService,
    private readonly networkOwnerService: NetworkOwnerService,
    readonly toastService: ToastService,
    readonly deviceCheckService: DeviceCheckService
  ) {
    super(toastService, deviceCheckService);
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.clearAllFilter();

    this.getNetworkOwners();
    this.getAreaContacts();
  }

  onRowEditInit(areacontact: AreaContactDto) {
    if (!this.hasValidationErrors && !this.isTableInEditMode) {
      areacontact.state = AreaContactSaveState.unknown;

      let index = this.tableValues.findIndex((x) => {
        return x.areaId == areacontact.areaId;
      });

      this.tableValues[index].state = AreaContactSaveState.unknown;

      this.clonedAreaContacts[areacontact.areaId] = { ...areacontact };

      this.isTableInEditMode = true;
    }
  }

  onRowEditSave(areacontact: AreaContactDto) {
    if (
      (!areacontact.firstName &&
        !areacontact.lastName &&
        !areacontact.email &&
        !areacontact.phoneNumber) ||
      (this.isCorrectFirstName(areacontact.firstName) &&
        this.isCorrectLastName(areacontact.lastName) &&
        this.isCorrectEmail(areacontact.email) &&
        this.isCorrectPhoneNumber(areacontact.phoneNumber))
    ) {
      delete this.clonedAreaContacts[areacontact.areaId];

      if (areacontact.ownerId)
        areacontact.ownerName = this.networkOwners.find(
          (x) => x.id === areacontact.ownerId
        ).name;

      this.hasValidationErrors = false;
      this.isTableInEditMode = false;

      this.save(areacontact);
    } else {
      this.hasValidationErrors = true;

      this.dataTable.initRowEdit(areacontact);

      this.showValidationErrorWhileSavingAreaInfo();
    }
  }

  onRowEditCancel(areacontact: AreaContactDto) {
    let index = this.tableValues.findIndex((x) => {
      return x.areaId == areacontact.areaId;
    });

    this.tableValues[index] = this.clonedAreaContacts[areacontact.areaId];

    delete this.clonedAreaContacts[areacontact.areaId];

    this.hasValidationErrors = false;
    this.isTableInEditMode = false;
  }

  onRowDeleteInit(areacontact: AreaContactDto) {
    this.deleteAreaContact(areacontact);

    this.hasValidationErrors = false;
    this.isTableInEditMode = false;
  }

  isCorrectFirstName(firstName: string) {
    return firstName && firstName.trim().length > 0;
  }

  isCorrectLastName(lastName: string) {
    return lastName && lastName.trim().length > 0;
  }

  isCorrectEmail(emailAddress: any) {
    let re = new RegExp(EmailRegexString);
    return emailAddress && re.test(String(emailAddress).toLowerCase());
  }

  isCorrectPhoneNumber(phoneNumber: any) {
    let re = new RegExp(PhoneNumberRegexString);
    return phoneNumber && re.test(String(phoneNumber).toLowerCase());
  }

  private showErrorWhileGettingAreaInfo() {
    this.toastService.showErrorToast(
      $localize`:@@general.error:Something went wrong`,
      $localize`:@@validation.area.contact.geterror:Something went wrong while retrieving the area contacts.`
    );
  }

  private showValidationErrorWhileSavingAreaInfo() {
    this.toastService.showErrorToast(
      $localize`:@@general.validation:Validation error`,
      $localize`:@@validation.area.contact.validation.error:The data of the area contact contains validation errors.`
    );
  }

  private showErrorWhileSavingAreaInfo() {
    this.toastService.showErrorToast(
      $localize`:@@general.validation:Something went wrong`,
      $localize`:@@validation.area.contact.save.geterror:Something went wrong while saving the area contact.`
    );
  }

  private showErrorWhileDeletingAreaContact() {
    this.toastService.showErrorToast(
      $localize`:@@general.validation:Something went wrong`,
      $localize`:@@validation.area.contact.delete.geterror:Something went wrong while deleting the area contact.`
    );
  }

  private showSuccessSavingAreaInfo() {
    this.toastService.showSuccesToast(
      $localize`:@@general.success:Successful`,
      $localize`:@@validation.area.contact.successfullysaved:The area contact is successfully saved.`
    );
  }

  private showSuccessDeletingAreaInfo() {
    this.toastService.showSuccesToast(
      $localize`:@@general.success:Successful`,
      $localize`:@@validation.area.contact.successfullydeleted:The area contact is successfully deleted.`
    );
  }

  private save(areacontact: AreaContactDto) {
    areacontact.state = AreaContactSaveState.ongoing;
    this.areaContactService.changeAreaContact(areacontact).subscribe(
      (res: any) => {
        areacontact.state = AreaContactSaveState.done;
        this.showSuccessSavingAreaInfo();
        setTimeout(() => {
          areacontact.state = AreaContactSaveState.unknown;
        }, 5000);
      },
      (err) => {
        areacontact.state = AreaContactSaveState.failed;
        this.showErrorWhileSavingAreaInfo();
      }
    );
  }

  private deleteAreaContact(areacontact: AreaContactDto) {
    const modalRef = this.modalService.open(ConfirmDialogComponent);
    modalRef.componentInstance.message =
      "Are you sure you want to delete the contact?";
    modalRef.componentInstance.decision
      .pipe(first())
      .subscribe((descision: boolean) => {
        if (descision) {
          areacontact.state = AreaContactSaveState.ongoing;
          this.areaContactService
            .deleteAreaContact(areacontact)
            .pipe(first())
            .subscribe(
              (res) => {
                areacontact.contactId = undefined;
                areacontact.firstName = undefined;
                areacontact.lastName = undefined;
                areacontact.email = undefined;
                areacontact.phoneNumber = undefined;
                areacontact.state = AreaContactSaveState.done;
                this.showSuccessDeletingAreaInfo();
                setTimeout(() => {
                  areacontact.state = AreaContactSaveState.unknown;
                }, 5000);
              },
              (err) => {
                areacontact.state = AreaContactSaveState.failed;
                this.showErrorWhileDeletingAreaContact();
              }
            );
        }
      });
  }

  private getNetworkOwners() {
    this.networkOwnerService
      .getNetworkOwners()
      .pipe(first())
      .subscribe((res) => (this.networkOwners = res));
  }

  private getAreaContacts() {
    this.areaContactService
      .getAreaContacts()
      .pipe(first())
      .subscribe(
        (res) => {
          res.forEach((x) => (x.state = AreaContactSaveState.unknown));
          this.tableValues = res;
          this.globalFilter$.next(this.globalFilter);
        },
        () => this.showErrorWhileGettingAreaInfo()
      );
  }
}
